import { getAPIErrorCode, useAmazonVendorAuthInitMutation } from '@vizit/api';
import type * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { AvcConnectModal } from '../avc-connect-modal';
import { type AvcAuthErrorReason, AvcConnectStep, toAvcAuthInitErrorReason } from '../avc-connect-modal.types';
import { AvcConnectContext, type AvcConnectContextValue } from './avc-connect.context';

interface AvcConnectProviderProps {
  children?: React.ReactNode;
}

export function AvcConnectProvider(props: AvcConnectProviderProps) {
  const { children } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<AvcConnectStep>(AvcConnectStep.Intro);
  const [errorReason, setErrorReason] = useState<AvcAuthErrorReason | undefined>(undefined);

  const authInit = useAmazonVendorAuthInitMutation();
  const isRedirecting = authInit.isPending;

  const open = useCallback(() => {
    setStep(AvcConnectStep.Intro);
    setErrorReason(undefined);
    setIsOpen(true);
  }, []);

  const openWithError = useCallback((reason: AvcAuthErrorReason) => {
    setStep(AvcConnectStep.Error);
    setErrorReason(reason);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (isRedirecting) return;
    setIsOpen(false);
    setStep(AvcConnectStep.Intro);
    setErrorReason(undefined);
    authInit.reset();
  }, [isRedirecting, authInit]);

  const startConnect = useCallback(() => {
    authInit.mutate(undefined, {
      onSuccess: ({ consent_url }) => {
        window.location.href = consent_url;
      },
      onError: (error) => {
        setStep(AvcConnectStep.Error);
        setErrorReason(toAvcAuthInitErrorReason(getAPIErrorCode(error)));
      },
    });
  }, [authInit]);

  const value = useMemo<AvcConnectContextValue>(
    () => ({
      isOpen,
      open,
      openWithError,
      close,
      step,
      errorReason,
      isRedirecting,
      startConnect,
    }),
    [isOpen, open, openWithError, close, step, errorReason, isRedirecting, startConnect],
  );

  return (
    <AvcConnectContext.Provider value={value}>
      {children}
      <AvcConnectModal />
    </AvcConnectContext.Provider>
  );
}
